// Formatting Utility Functions

/*
 * Dates. The portal used to render the same instant three ways depending on which
 * screen you were on: formatDate() pinned to en-US ("Aug 20, 2026, 08:24 PM"), bare
 * toLocaleString() following the browser's locale ("20/08/2026, 20:24"), and
 * formatTimeAgo() ("5 days ago"). On a non-US machine a record could show one format
 * in a table and another in its own detail panel.
 *
 * Everything now goes through the two formatters below. They render an explicit month
 * name so no reader has to guess whether 08/09 is August or September, and timestamps
 * carry UTC because this data is an audit trail — an instant that reads differently
 * depending on who opens it is worse than useless in a compliance context.
 */

const DATE_ONLY = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC'
});
const TIME_ONLY = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC'
});

function toDate(value) {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Calendar date, no time: "20 Aug 2026".
 * Use for things that are a day rather than an instant — expiry, due date, created-on.
 * @param {string|Date} value
 * @returns {string}
 */
export function formatDateOnly(value) {
    const d = toDate(value);
    return d ? DATE_ONLY.format(d) : '-';
}

/**
 * Instant, in UTC: "20 Aug 2026 20:24 UTC".
 * Use for anything that happened at a moment — audit entries, activations, revocations.
 * @param {string|Date} value
 * @returns {string}
 */
export function formatDateTime(value) {
    const d = toDate(value);
    return d ? `${DATE_ONLY.format(d)} ${TIME_ONLY.format(d)} UTC` : '-';
}

/**
 * @deprecated Call formatDateTime or formatDateOnly directly — the distinction
 * between "a day" and "an instant" is worth stating at the call site.
 * @param {string|Date} value
 * @returns {string}
 */
export function formatDate(value) {
    return formatDateTime(value);
}

/**
 * Relative time for recent activity ("5 days ago"), falling back to an absolute
 * date beyond a week. Pair it with a title attribute carrying formatDateTime so the
 * exact instant is always one hover away.
 * @param {Date} date - Date to format
 * @returns {string} - Relative time string
 */
export function formatTimeAgo(date) {
    const d = toDate(date);
    if (!d) return '-';
    const seconds = Math.floor((new Date() - d) / 1000);

    if (seconds < 0) return formatDateOnly(d);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) {
        const mins = Math.floor(seconds / 60);
        return `${mins} ${mins === 1 ? 'minute' : 'minutes'} ago`;
    }
    if (seconds < 86400) {
        const hours = Math.floor(seconds / 3600);
        return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    }
    if (seconds < 604800) {
        const days = Math.floor(seconds / 86400);
        return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    }

    return formatDateOnly(d);
}

/**
 * Format currency
 * @param {number} amount - Amount to format
 * @param {string} [currency='USD'] - Currency code
 * @returns {string} - Formatted currency string
 */
export function formatCurrency(amount, currency = 'USD') {
    if (amount === undefined || amount === null) return '$0.00';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase()
    }).format(amount);
}

/**
 * Format feature name for display (camelCase to Title Case)
 * @param {string} key - Feature key to format
 * @returns {string} - Formatted feature name
 */
export function formatFeatureName(key) {
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
}

/**
 * Format feature value for display
 * @param {*} value - Value to format
 * @returns {string} - Formatted value
 */
export function formatFeatureValue(value) {
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }
    if (value === -1) {
        return 'Unlimited';
    }
    return value;
}
